import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey,
  date
} from "drizzle-orm/pg-core";
import { carriers } from './carriers';
import { claims } from './claims';
import { contacts } from './contacts';
import { claimTypeEnum, decisionTypeEnum, feeTypeEnum, leadCampaignEnum, leadSourceEnum, leadStatusEnum, paRoleEnum, perilTypeEnum, propertyTypeEnum, taskPriorityEnum } from './enums';
import { organizations } from './organizations';
import { users } from './users';

// Leads table
export const leads = pgTable("leads", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Lead basic information
  claimType: claimTypeEnum("claim_type").default("residential"), // Residential (individual) or Commercial (business)
  firstName: varchar("first_name"), // Required for residential, null for commercial
  lastName: varchar("last_name"), // Required for residential, null for commercial
  email: varchar("email"),
  phone: varchar("phone"),
  company: varchar("company"), // Required for commercial, null for residential
  pointOfContact: varchar("point_of_contact"), // For commercial leads - the contact person at the company
  
  // Property and loss information
  propertyAddress: text("property_address"),
  propertyCity: varchar("property_city"),
  propertyState: varchar("property_state"),
  propertyZip: varchar("property_zip"),
  propertyType: propertyTypeEnum("property_type"),
  
  // Loss details
  dateOfLoss: date("date_of_loss"),
  perilType: perilTypeEnum("peril_type"),
  lossDescription: text("loss_description"),
  estimatedValue: decimal("estimated_value", { precision: 12, scale: 2 }),
  
  // Lead tracking
  status: leadStatusEnum("status").notNull().default("new"),
  source: leadSourceEnum("source").notNull().default("other"),
  campaign: leadCampaignEnum("campaign"),
  referrerSource: varchar("referrer_source"),
  referrerId: uuid("referrer_id").references(() => contacts.id),
  assignedAdjusterId: varchar("assigned_adjuster_id").references(() => users.id),
  
  // Decision tracking
  decisionType: decisionTypeEnum("decision_type"),
  decisionMadeAt: timestamp("decision_made_at"),
  decisionMadeBy: varchar("decision_made_by").references(() => users.id),
  decisionNotes: text("decision_notes"),
  
  // Insurance information
  carrierId: uuid("carrier_id").references(() => carriers.id),
  policyNumber: varchar("policy_number"),
  claimNumber: varchar("claim_number"), // Carrier-assigned claim number from insurance paperwork

  
  // Contact tracking
  lastContactDate: timestamp("last_contact_date"),
  nextFollowUpDate: timestamp("next_follow_up_date"),
  
  // Conversion tracking
  convertedToClaimId: uuid("converted_to_claim_id").references(() => claims.id, { onDelete: "set null" }),
  convertedAt: timestamp("converted_at"),
  
  // Metadata
  notes: text("notes"),
  intakeDraftData: jsonb("intake_draft_data"), // Saved intake form draft (all fields before evaluation)
  priority: taskPriorityEnum("priority").default("medium"),
  
  // Lead evaluation results
  evaluationScore: integer("evaluation_score"), // Overall score 0-100
  paEligible: boolean("pa_eligible"),
  appraisalEligible: boolean("appraisal_eligible"),
  recommendedAction: varchar("recommended_action"), // accept, reject, request_more_info, consultation
  eligibilityReasons: jsonb("eligibility_reasons"), // Array of reasons
  riskFactors: jsonb("risk_factors"), // Array of risk factors
  nextSteps: jsonb("next_steps"), // Array of next steps
  licensingNotes: text("licensing_notes"),
  estimatedRecovery: decimal("estimated_recovery", { precision: 12, scale: 2 }),
  evaluatedAt: timestamp("evaluated_at"),
  evaluatedBy: varchar("evaluated_by").references(() => users.id),
  
  // AI evaluation results
  aiScore: integer("ai_score"), // AI-generated score 0-100
  aiConfidence: varchar("ai_confidence"), // high, medium, low
  successProbability: decimal("success_probability", { precision: 5, scale: 4 }), // 0-1
  estimatedTimeToClose: integer("estimated_time_to_close"), // days
  finalDecision: varchar("final_decision"), // strongly_recommend, recommend, neutral, caution, reject
  aiInsights: jsonb("ai_insights"), // Historical patterns and recommendations
  lastAiAnalysis: timestamp("last_ai_analysis"),
  
  // Manual override tracking
  manualOverride: boolean("manual_override").default(false),
  overrideReason: text("override_reason"),
  overrideBy: varchar("override_by").references(() => users.id),
  overrideAt: timestamp("override_at"),
  
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_leads_status").on(table.status),
  index("idx_leads_assigned_adjuster").on(table.assignedAdjusterId),
  index("idx_leads_source").on(table.source),
  index("idx_leads_referrer_id").on(table.referrerId),
  index("idx_leads_created_at").on(table.createdAt),
  index("idx_leads_decision_type").on(table.decisionType),
]);


// Lead Feedback - for AI learning from outcomes
export const leadFeedbacks = pgTable("lead_feedbacks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  leadId: uuid("lead_id").references(() => leads.id, { onDelete: "cascade" }).notNull(),
  outcome: varchar("outcome").notNull(), // 'won', 'lost', 'closed_no_recovery', 'in_progress'
  actualRecovery: varchar("actual_recovery"), // Actual recovery amount
  timeToClose: integer("time_to_close"), // Days from lead to close
  reasonForOutcome: text("reason_for_outcome"),
  profitability: decimal("profitability", { precision: 5, scale: 2 }), // Profit score
  clientSatisfaction: integer("client_satisfaction"), // 1-5 scale
  wouldAcceptAgain: boolean("would_accept_again"),
  notes: text("notes"),
  recordedAt: timestamp("recorded_at").defaultNow(),
  recordedBy: varchar("recorded_by").references(() => users.id),
}, (table) => [
  index("idx_lead_feedbacks_lead_id").on(table.leadId),
  index("idx_lead_feedbacks_outcome").on(table.outcome),
  index("idx_lead_feedbacks_recorded_at").on(table.recordedAt),
]);

// Lead PA Assignments — mirrors claimPaAssignments; tracks Sales PA per lead with commission splits
export const leadPaAssignments = pgTable("lead_pa_assignments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  leadId: uuid("lead_id").references(() => leads.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }), // null = external PA
  role: paRoleEnum("role").notNull().default("sales_pa"),
  customRoleName: varchar("custom_role_name"),
  paName: varchar("pa_name"), // fallback name when userId is null
  commissionFeeType: feeTypeEnum("commission_fee_type").default("percent"),
  commissionPercentage: decimal("commission_percentage", { precision: 5, scale: 2 }).notNull().default("0"),
  commissionFlatAmount: decimal("commission_flat_amount", { precision: 12, scale: 2 }),
  commissionHourlyRate: decimal("commission_hourly_rate", { precision: 8, scale: 2 }),
  notes: text("notes"),
  isActive: boolean("is_active").default(true),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_lead_pa_assignments_lead").on(table.leadId),
  index("idx_lead_pa_assignments_user").on(table.userId),
  index("idx_lead_pa_assignments_role").on(table.role),
]);
